import { collection, onSnapshot, query, where, Timestamp, type Unsubscribe } from 'firebase/firestore';
import { db } from '../firebase';

// Forma real de los documentos que escribe `ProjectMessage`
// (backend/services/core/internal/projection/firestore.go) — deliberadamente
// más flaca que `Conversation`/`Message` de la REST: sin `unreadCount`, sin
// `senderDisplayName`, y el adjunto es solo un booleano `hasAttachment`,
// nunca su ficha (id/contentType/tamaño). Por eso estas suscripciones NO se
// usan para pintar directamente — son la señal de "algo cambió" que dispara
// un refetch por REST, que es quien trae la forma completa (ver ChatContext).
export interface ConversationLiveDoc {
  id: string;
  participantUids: string[];
  updatedAt?: string;
}

export interface MessageLiveDoc {
  id: string; // = messageId
  senderUid: string;
  participantUids: string[];
  hasAttachment: boolean;
  sentAt?: string;
}

function toIso(value: unknown): string | undefined {
  return value instanceof Timestamp ? value.toDate().toISOString() : undefined;
}

/**
 * Lista de conversaciones del tenant, en vivo. El Administrador va SIN
 * filtro —no está en `participantUids`, EP-55-chat-persistido.md §5.2— y
 * aplicarle el mismo `array-contains` no falla: devuelve CERO conversaciones
 * en silencio. El resto de roles exige el filtro, **inline** y nunca en una
 * función de apoyo, o la consulta falla entera (medido contra el emulador,
 * §5.1 del mismo doc).
 */
export function subscribeConversationsLive(
  tenantId: string,
  uid: string,
  isAdmin: boolean,
  onUpdate: (docs: ConversationLiveDoc[]) => void,
  onError: (error: Error) => void,
): Unsubscribe {
  const coll = collection(db, `tenants/${tenantId}/conversations`);
  const target = isAdmin ? coll : query(coll, where('participantUids', 'array-contains', uid));

  return onSnapshot(
    target,
    snapshot => onUpdate(snapshot.docs.map(d => {
      const data = d.data();
      return {
        id: d.id,
        participantUids: (data.participantUids as string[]) ?? [],
        updatedAt: toIso(data.updatedAt),
      };
    })),
    onError,
  );
}

/**
 * Mensajes de UNA conversación abierta, en vivo. Misma trampa que la lista:
 * la subcolección exige el filtro aunque todos sus documentos compartan
 * participantes — Firestore decide si la consulta es segura ANTES de leer,
 * así que no le sirve que todos fueran a resultar permitidos.
 */
export function subscribeMessagesLive(
  tenantId: string,
  conversationId: string,
  uid: string,
  isAdmin: boolean,
  onUpdate: (docs: MessageLiveDoc[]) => void,
  onError: (error: Error) => void,
): Unsubscribe {
  const coll = collection(db, `tenants/${tenantId}/conversations/${conversationId}/messages`);
  const target = isAdmin ? coll : query(coll, where('participantUids', 'array-contains', uid));

  return onSnapshot(
    target,
    snapshot => onUpdate(snapshot.docs.map(d => {
      const data = d.data();
      return {
        id: d.id,
        senderUid: data.senderUid as string,
        participantUids: (data.participantUids as string[]) ?? [],
        hasAttachment: Boolean(data.hasAttachment),
        sentAt: toIso(data.sentAt),
      };
    })),
    onError,
  );
}
